import './WebhookForm.scss';

import { FlightButton, FlightSelect, FlightTextArea, FlightTextInput } from '@flybits/design-system';

import React from 'react';
import { ReactComponent as SendGridProvider } from 'assets/icons/provider-sendgrid.svg';
import { ReactComponent as TwilioProvider } from 'assets/images/provider-twilio.svg';

type MethodOption = { key: string; name: string };
const methodOptions: MethodOption[] = [
  {
    key: 'POST',
    name: 'POST',
  },
  {
    key: 'PUT',
    name: 'PUT',
  },
  {
    key: 'DELETE',
    name: 'DELETE',
  },
];

export type WebhookFormValues = {
  name: string;
  description: string;
  method: string;
  url: string;
  headers: { key: string; value: string }[];
  payload: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sendgrid?: Record<string, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  twilio?: Record<string, any>;
  certificateName: string;
};

type WebhookFormProps = {
  formValues: WebhookFormValues;
  setFormValues: React.Dispatch<React.SetStateAction<WebhookFormValues>>;
  isViewOnly: boolean;
};

function WebhookForm({ formValues, setFormValues, isViewOnly }: WebhookFormProps) {
  return (
    <div className={isViewOnly ? 'webhook-form webhook-form--is-view-only' : 'webhook-form'}>
      <div className="layout-left">
        <label className="form-label" htmlFor="wh-name">
          Name
        </label>

        <FlightTextInput
          label="Enter name"
          labelId="wh-name"
          width="100%"
          value={formValues.name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setFormValues((prev) => ({ ...prev, name: e.target.value }));
          }}
        />

        {formValues.sendgrid && (
          <div className="section-sendgrid">
            <h4>
              <SendGridProvider /> SendGrid
            </h4>

            <label className="form-label" htmlFor="wh-recipient-email">
              To<span>*</span>
            </label>
            <FlightTextInput
              placeholderText="{{ctx.sdk.user.email}}"
              labelId="wh-recipient-email"
              width="100%"
              value={formValues.sendgrid.to}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFormValues((prev) => ({
                  ...prev,
                  sendgrid: {
                    ...prev.sendgrid!,
                    to: e.target.value,
                    templateName: prev.sendgrid?.templateName ?? '',
                    templateId: prev.sendgrid?.templateId ?? '',
                    templateData: prev.sendgrid?.templateData ?? [],
                  },
                }));
              }}
            />

            <label className="form-label" htmlFor="wh-template-id">
              Template ID<span>*</span>
            </label>
            <FlightTextInput
              placeholderText="d-c3138e9433e24761bd4e8398984c8c37"
              labelId="wh-template-id"
              width="100%"
              value={formValues.sendgrid.templateId}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFormValues((prev) => ({
                  ...prev,
                  sendgrid: {
                    ...prev.sendgrid!,
                    templateId: e.target.value,
                    templateName: prev.sendgrid?.templateName ?? '',
                    templateData: prev.sendgrid?.templateData ?? [],
                  },
                }));
              }}
            />

            <label className="form-label" htmlFor="wh-template-name">
              Template Name
            </label>
            <FlightTextInput
              placeholderText="Hello Email"
              labelId="wh-template-name"
              width="100%"
              value={formValues.sendgrid.templateName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFormValues((prev) => ({
                  ...prev,
                  sendgrid: {
                    ...prev.sendgrid!,
                    templateName: e.target.value,
                    templateId: prev.sendgrid?.templateId ?? '',
                    templateData: prev.sendgrid?.templateData ?? [],
                  },
                }));
              }}
            />

            <p className="section-label">Template Data</p>
            <div className="section-template-data section-request-headers">
              {formValues.sendgrid.templateData.map(
                (templateDataItem: { key: unknown; value: unknown }, idx: unknown) => (
                  <div key={`wh-sendgrid-templated-data-${idx}`} className="row">
                    <FlightTextInput
                      label="Key"
                      value={String(templateDataItem.key ?? '')}
                      width="100%"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFormValues((prev) => ({
                          ...prev,
                          sendgrid: {
                            ...prev.sendgrid!,
                            templateData: prev.sendgrid!.templateData.map((item: any, i: unknown) =>
                              i === idx ? { ...item, key: e.target.value } : item,
                            ),
                            templateId: prev.sendgrid?.templateId ?? '',
                            templateName: prev.sendgrid?.templateName ?? '',
                          },
                        }));
                      }}
                    />
                    <FlightTextInput
                      label="Value"
                      value={String(templateDataItem.value ?? '')}
                      width="100%"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFormValues((prev) => ({
                          ...prev,
                          sendgrid: {
                            ...prev.sendgrid!,
                            templateData: prev.sendgrid!.templateData.map((item: any, i: unknown) =>
                              i === idx ? { ...item, value: e.target.value } : item,
                            ),
                            templateId: prev.sendgrid?.templateId ?? '',
                            templateName: prev.sendgrid?.templateName ?? '',
                          },
                        }));
                      }}
                    />
                    <FlightButton
                      label="Remove"
                      iconLeft="remove"
                      theme="secondary"
                      onClick={() =>
                        setFormValues((prev) => ({
                          ...prev,
                          sendgrid: {
                            ...prev.sendgrid!,
                            templateData: prev.sendgrid!.templateData.filter((_: any, i: unknown) => i !== idx),
                            templateId: prev.sendgrid?.templateId ?? '',
                            templateName: prev.sendgrid?.templateName ?? '',
                          },
                        }))
                      }
                    />
                  </div>
                ),
              )}
              <FlightButton
                label="Add Data"
                iconLeft="add"
                onClick={() =>
                  setFormValues((prev) => {
                    const newData = JSON.parse(JSON.stringify(prev));
                    newData.sendgrid.templateData.push({ key: '', value: '' });
                    return newData;
                  })
                }
              ></FlightButton>
            </div>
          </div>
        )}

        {formValues.twilio && (
          <div className="section-twilio">
            <h4>
              <TwilioProvider /> Twilio
            </h4>

            <label className="form-label" htmlFor="wh-recipient-phone">
              To<span>*</span>
            </label>
            <FlightTextInput
              placeholderText="{{ctx.sdk.user.phoneNumber}}"
              labelId="wh-recipient-phone"
              width="100%"
              value={formValues.twilio.to}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFormValues((prev) => ({
                  ...prev,
                  twilio: {
                    ...prev.twilio!,
                    to: e.target.value,
                    templateName: prev.twilio?.templateName ?? '',
                    templateId: prev.twilio?.templateId ?? '',
                    templateData: prev.twilio?.templateData ?? [],
                  },
                }));
              }}
            />

            <label className="form-label" htmlFor="wh-template-id">
              Template ID<span>*</span>
            </label>
            <FlightTextInput
              placeholderText="HX72569997728c382f94f7efc237dbd097"
              labelId="wh-template-id"
              width="100%"
              value={formValues.twilio.templateId}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFormValues((prev) => ({
                  ...prev,
                  twilio: {
                    ...prev.twilio!,
                    templateId: e.target.value,
                    templateName: prev.twilio?.templateName ?? '',
                    templateData: prev.twilio?.templateData ?? [],
                  },
                }));
              }}
            />

            <label className="form-label" htmlFor="wh-template-name">
              Template Name
            </label>
            <FlightTextInput
              placeholderText="Hello SMS"
              labelId="wh-template-name"
              width="100%"
              value={formValues.twilio.templateName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFormValues((prev) => ({
                  ...prev,
                  twilio: {
                    ...prev.twilio!,
                    templateName: e.target.value,
                    templateId: prev.twilio?.templateId ?? '',
                    templateData: prev.twilio?.templateData ?? [],
                  },
                }));
              }}
            />

            <p className="section-label">Template Data</p>
            <div className="section-template-data section-request-headers">
              {formValues.twilio.templateData.map(
                (templateDataItem: { key: unknown; value: unknown }, idx: unknown) => (
                  <div key={`wh-twilio-templated-data-${idx}`} className="row">
                    <FlightTextInput
                      label="Key"
                      value={String(templateDataItem.key ?? '')}
                      width="100%"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFormValues((prev) => ({
                          ...prev,
                          twilio: {
                            ...prev.twilio!,
                            templateData: prev.twilio!.templateData.map((item: any, i: unknown) =>
                              i === idx ? { ...item, key: e.target.value } : item,
                            ),
                            templateId: prev.twilio?.templateId ?? '',
                            templateName: prev.twilio?.templateName ?? '',
                          },
                        }));
                      }}
                    />
                    <FlightTextInput
                      label="Value"
                      value={String(templateDataItem.value ?? '')}
                      width="100%"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFormValues((prev) => ({
                          ...prev,
                          twilio: {
                            ...prev.twilio!,
                            templateData: prev.twilio!.templateData.map((item: any, i: unknown) =>
                              i === idx ? { ...item, value: e.target.value } : item,
                            ),
                            templateId: prev.twilio?.templateId ?? '',
                            templateName: prev.twilio?.templateName ?? '',
                          },
                        }));
                      }}
                    />
                    <FlightButton
                      label="Remove"
                      iconLeft="remove"
                      theme="secondary"
                      onClick={() =>
                        setFormValues((prev) => ({
                          ...prev,
                          twilio: {
                            ...prev.twilio!,
                            templateData: prev.twilio!.templateData.filter((_: any, i: unknown) => i !== idx),
                            templateId: prev.twilio?.templateId ?? '',
                            templateName: prev.twilio?.templateName ?? '',
                          },
                        }))
                      }
                    />
                  </div>
                ),
              )}
              <FlightButton
                label="Add Data"
                iconLeft="add"
                onClick={() =>
                  setFormValues((prev) => {
                    const newData = JSON.parse(JSON.stringify(prev));
                    newData.twilio.templateData.push({ key: '', value: '' });
                    return newData;
                  })
                }
              ></FlightButton>
            </div>
          </div>
        )}

        {formValues.sendgrid == null && formValues.twilio == null && (
          <div className="section-webhook">
            <label className="form-label" htmlFor="wh-name">
              Description
            </label>
            <FlightTextArea
              label="Description"
              value={formValues.description}
              width="100%"
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                setFormValues((prev) => ({ ...prev, description: e.target.value }));
              }}
            />

            <label className="form-label" htmlFor="wh-request-url">
              Request URL<span>*</span>
            </label>
            <div className="section-request-url">
              <FlightSelect
                label="Method"
                width="120px"
                options={methodOptions}
                selected={methodOptions.find((method) => method.key === formValues.method) ?? methodOptions[0]}
                handleOptionClick={(option: MethodOption) => {
                  setFormValues((prev) => ({
                    ...prev,
                    method: option.key,
                  }));
                }}
              />
              <FlightTextInput
                label="Enter request URL"
                labelId="wh-request-url"
                width="100%"
                value={formValues.url}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setFormValues((prev) => ({ ...prev, url: e.target.value }));
                }}
              />
            </div>

            <p className="section-label">
              Request Headers<span>*</span>
            </p>
            <div className="section-request-headers">
              {formValues.headers.map((header, idx) => (
                <div key={`wh-rh-k-${idx}`} className="row">
                  <FlightTextInput
                    label="Key"
                    value={header.key}
                    width="100%"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setFormValues((prev) => ({
                        ...prev,
                        headers: prev.headers.map((rh, rhi) => {
                          if (rhi !== idx) return rh;
                          return { ...rh, key: e.target.value };
                        }),
                      }));
                    }}
                  />
                  <FlightTextInput
                    label="Value"
                    value={header.value}
                    width="100%"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setFormValues((prev) => ({
                        ...prev,
                        headers: prev.headers.map((rh, rhi) => {
                          if (rhi !== idx) return rh;
                          return { ...rh, value: e.target.value };
                        }),
                      }));
                    }}
                  />
                  <FlightButton
                    label="Remove"
                    iconLeft="remove"
                    theme="secondary"
                    onClick={() =>
                      setFormValues((prev) => ({
                        ...prev,
                        headers: prev.headers.filter((_, i) => i !== idx),
                      }))
                    }
                  ></FlightButton>
                </div>
              ))}
              <FlightButton
                label="Add Request Header"
                iconLeft="add"
                onClick={() =>
                  setFormValues((prev) => ({ ...prev, headers: [...prev.headers, { key: '', value: '' }] }))
                }
              ></FlightButton>
            </div>

            <p className="section-label">Payload</p>
            <FlightTextArea
              className="payload"
              label="Payload"
              value={formValues.payload}
              width="100%"
              maxLength={5000}
              hideCharCounter={true}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                setFormValues((prev) => ({ ...prev, payload: e.target.value }));
              }}
            />
          </div>
        )}
      </div>

      {formValues.sendgrid == null && formValues.twilio == null && <div className="layout-center">&nbsp;</div>}

      {formValues.sendgrid == null && formValues.twilio == null && (
        <div className="layout-right">
          <p className="section-label">Certificate</p>
          <p>To make changes, please visit the developer portal.</p>
          <FlightTextInput
            label="Certificate"
            value={formValues.certificateName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFormValues((prev) => ({ ...prev, certificateName: e.target.value }));
            }}
          />
        </div>
      )}
    </div>
  );
}

export default WebhookForm;
